import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, from, of, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { IDataSource } from '../models/form-template';
import { DataGqlServiceLite } from '../../shared/services/data-gql.service.lite';
import { GQL_QUERY_REGISTRY } from '../gql/data-source-queries';
import { environment } from '../../../environments/environment';

/**
 * Resolves IDataSource definitions (from a FormTemplate) into Observable<any[]>.
 *
 * Supports three source types:
 *  - 'graphql'  -> executes a named query from GQL_QUERY_REGISTRY
 *  - 'rest'     -> HTTP GET built from endpoint + baseUrl token + queryParams
 *  - 'static'   -> returns configuration.items as-is
 *
 * Static results are cached in-memory by sourceKey (shareReplay).
 * Dynamic results (those using dynamicParams + a context) are not cached -
 * a fresh request is made each time resolve() is called with a context.
 */
@Injectable({ providedIn: 'root' })
export class DynamicFormDataSourceService {

  private readonly cache = new Map<string, Observable<any[]>>();

  /** Environment base-URL tokens supported in IDataSourceConfiguration.baseUrl */
  private readonly baseUrlMap: Record<string, string> = {
    taskManagerServiceEndPoint: environment.taskManagerServiceEndPoint,
    gQLEndpoint: environment.gQLEndpoint,
  };

  constructor(
    private readonly gqlService: DataGqlServiceLite,
    private readonly http: HttpClient
  ) {}

  /**
   * Resolves a data source to an Observable<any[]>.
   *
   * @param dataSource  The IDataSource definition from the form template.
   * @param context     Live form values. Required when the configuration
   *                    declares `dynamicParams` - those field values are
   *                    appended as query-string parameters on each call.
   *                    When context is provided the result is NOT cached.
   */
  resolve(dataSource: IDataSource, context?: Record<string, any>): Observable<any[]> {
    // configuration may arrive as a JSON string from the API — normalise it once.
    const ds: IDataSource = {
      ...dataSource,
      configuration: typeof (dataSource.configuration as any) === 'string'
        ? JSON.parse(dataSource.configuration as any)
        : dataSource.configuration
    };

    const hasDynamicParams =
      ds.sourceType === 'rest' &&
      (ds.configuration?.dynamicParams?.length ?? 0) > 0 &&
      context != null;

    // Skip cache for dynamic-param requests so fresh data is fetched each time
    if (!hasDynamicParams) {
      const cached = this.cache.get(ds.sourceKey);
      if (cached) return cached;
    }

    const obs$ = this.fetchData(ds, context).pipe(shareReplay({ bufferSize: 1, refCount: false }));

    if (!hasDynamicParams) {
      this.cache.set(ds.sourceKey, obs$);
    }

    return obs$;
  }

  /** Clears the in-memory cache (call when the form type changes). */
  clearCache(): void {
    this.cache.clear();
  }

  // -- private ---------------------------------------------------------------

  private fetchData(ds: IDataSource, context?: Record<string, any>): Observable<any[]> {
    if (!ds.isActive) return of([]);

    switch (ds.sourceType) {
      case 'graphql': return this.fetchGraphql(ds);
      case 'rest':    return this.fetchRest(ds, context);
      case 'static':  return of(ds.configuration?.items ?? []);
      default:
        console.warn(`[DynamicFormDataSourceService] Unknown sourceType "${ds.sourceType}" for key "${ds.sourceKey}"`);
        return of([]);
    }
  }

  private fetchGraphql(ds: IDataSource): Observable<any[]> {
    const queryName = ds.configuration?.query;
    const queryDoc = queryName ? GQL_QUERY_REGISTRY[queryName] : null;

    if (!queryDoc) {
      console.warn(
        `[DynamicFormDataSourceService] No GQL query registered for "${queryName}". ` +
        `Add it to src/app/framework/gql/data-source-queries.ts.`
      );
      return of([]);
    }

    return from(this.gqlService.query<any>(queryDoc, {})).pipe(
      map(data => this.unwrapResponse(data, ds.configuration?.responseKey))
    );
  }

  private fetchRest(ds: IDataSource, context?: Record<string, any>): Observable<any[]> {
    const cfg = ds.configuration;
    if (!cfg?.endpoint) {
      console.warn(`[DynamicFormDataSourceService] REST source "${ds.sourceKey}" has no endpoint.`);
      return of([]);
    }

    const base = this.resolveBaseUrl(cfg.baseUrl);
    const url = `${base}/${cfg.endpoint}`;

    let params = new HttpParams();

    // Static params declared in config
    if (cfg.queryParams) {
      Object.entries(cfg.queryParams).forEach(([k, v]) => {
        params = params.set(k, v);
      });
    }

    // Dynamic params resolved from live form context
    if (cfg.dynamicParams?.length && context) {
      cfg.dynamicParams.forEach((key: string) => {
        const value = context[key];
        if (value != null && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http.get<any>(url, { params }).pipe(
      map(data => this.unwrapResponse(data, cfg.responseKey))
    );
  }

  /**
   * Resolves a baseUrl token to its environment value.
   * Falls back to treating the value as a literal URL if unrecognised.
   */
  private resolveBaseUrl(token?: string): string {
    if (!token) return environment.taskManagerServiceEndPoint;
    return this.baseUrlMap[token] ?? token;
  }

  /**
   * Unwraps a potentially nested response to a plain array.
   *
   * Resolution order:
   *  1. responseKey set => traverse dot-notation path (e.g. "result" or "data.items")
   *     - if the resolved value is an array, return it
   *     - if it isn't (bad key / mismatch), fall through to auto-detect rather
   *       than returning [] so a misconfigured responseKey degrades gracefully
   *  2. Root is already an array => return it
   *  3. Otherwise => return the first array-valued property found on the object
   */
  private unwrapResponse(data: any, responseKey?: string): any[] {
    if (!data) return [];

    if (responseKey) {
      const value = responseKey.split('.').reduce((acc: any, key: string) => acc?.[key], data);
      if (Array.isArray(value)) return value;
      // Key didn't resolve to an array — log a warning and fall through
      console.warn(
        `[DynamicFormDataSourceService] responseKey "${responseKey}" did not resolve to an array. ` +
        `Falling back to auto-detect. Check your data source configuration.`
      );
    }

    if (Array.isArray(data)) return data;

    const firstArrayKey = Object.keys(data).find(k => Array.isArray(data[k]));
    return firstArrayKey ? data[firstArrayKey] : [];
  }
}
